import React, { useState, useEffect, useRef } from 'react';
import { X, Wand2, Youtube } from 'lucide-react';
import { fetchWithAuth, API_URL } from '../services/ApiService';

const GENERATING_MESSAGES = [
  'Analyzing song structure...',
  'Identifying chord progressions...',
  'Transcribing lyrics...',
  'Building sections...',
  'Assembling band sheet...'
];

const QuickDraftModal = ({ isOpen, onClose, onSuccess }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [state, setState] = useState('idle'); // idle | generating | error
  const [progressMessage, setProgressMessage] = useState('');
  const messageIndexRef = useRef(0);
  const intervalRef = useRef(null);

  // Rotate progress messages during generation
  useEffect(() => {
    if (state === 'generating') {
      messageIndexRef.current = 0;
      setProgressMessage(GENERATING_MESSAGES[0]);
      intervalRef.current = setInterval(() => {
        messageIndexRef.current = (messageIndexRef.current + 1) % GENERATING_MESSAGES.length;
        setProgressMessage(GENERATING_MESSAGES[messageIndexRef.current]);
      }, 3000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [state]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setError('');
      setState('idle');
    }
  }, [isOpen]);

  const validateYouTubeUrl = (value) => {
    if (!value) return false;
    const ytRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)/;
    return ytRegex.test(value);
  };

  const handleGenerate = async () => {
    if (!validateYouTubeUrl(url)) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    setError('');
    setState('generating');

    try {
      const response = await fetchWithAuth(`${API_URL}/quick-draft/generate`, {
        method: 'POST',
        body: JSON.stringify({ youtubeUrl: url.trim() })
      });

      if (response.success && response.data) {
        setState('idle');
        onSuccess(response.data);
      } else {
        throw new Error(response.error || 'Generation failed');
      }
    } catch (err) {
      setState('error');
      setError(err.message || 'Failed to generate draft. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">Quick Draft</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Close"
            disabled={state === 'generating'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Generate a band sheet from a YouTube URL using AI. This creates a draft with lyrics, chords, and song structure.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-xs text-amber-800">
              This is an AI-generated draft — structure and chords may need manual adjustment.
            </p>
          </div>

          <div>
            <label htmlFor="quick-draft-url" className="block text-sm font-medium text-gray-700 mb-2">
              YouTube URL
            </label>
            <div className="relative">
              <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-600" />
              <input
                id="quick-draft-url"
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setError(''); }}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={state === 'generating'}
              />
            </div>
          </div>

          {state === 'generating' && (
            <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                <p className="text-sm text-purple-800">{progressMessage}</p>
              </div>
            </div>
          )}

          {error && state !== 'generating' && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={state === 'generating'}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={state === 'generating' || !url.trim()}
            className={`flex-1 px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 ${
              state === 'generating' || !url.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            <Wand2 className="w-4 h-4" />
            {state === 'generating' ? 'Generating...' : 'Generate Draft'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickDraftModal;
